import { BuiltInPluginIds } from '@squaredup/ids';
import { sortBy } from 'lodash';
import { DataStream } from 'services/DataStreamDefinitionService';
import { PluginSourceConfig } from 'services/SourceConfigService';

export type SplashScreenDataSource = {
    id: string;
    name: string;
    tooltipText?: React.ReactNode;
    failedImports?: { configId: string; displayName: string }[];
};

/**
 * Returns a list of data sources sorted alphabetically to show on the splash screen, excluding built-in plugins.
 * Will also include failed imports for plugins which have never had a successful import.
 */
export const useSplashScreenDatasources = (datasources: PluginSourceConfig[], availableDataStreams: DataStream[]) => {
    // Find plugins that have dataStreams available to be used.
    const dataStreamPluginIds = new Set(availableDataStreams.map((ds) => ds.pluginId as string));

    const splashScreenDatasources = datasources.reduce((acc, datasource) => {
        const { plugin, importStatus, lastImportVersion } = datasource;
        const hasImportSucceeded = importStatus?.status !== 'failed' || lastImportVersion;

        if (!plugin?.pluginId || !plugin.displayName) {
            return acc;
        }

        const isBuiltIn = BuiltInPluginIds.includes(plugin.pluginId);
        const hasAvailableDatastreams = dataStreamPluginIds.has(plugin.pluginId);

        /**
         * Exclude:
         * 1. Built-in plugins
         * 2. Successfully imported plugins without datastreams (e.g., Geodata)
         *
         * Note: Failed imports that normally provide datastreams should still show
         * as they may not appear in availableDatastreams list.
         */
        if (isBuiltIn || (hasImportSucceeded && !hasAvailableDatastreams)) {
            return acc;
        }

        const instanceConfig = {
            configId: datasource.id,
            displayName: datasource.displayName
        };

        // Check if the plugin has already been added to the splash screen list
        const foundDataSource = acc.find((ds) => ds.id === plugin.pluginId);

        if (!foundDataSource) {
            acc.push({
                id: plugin.pluginId,
                name: plugin.displayName,
                failedImports: !hasImportSucceeded ? [instanceConfig] : undefined
            });
        }

        /**
         * 1. If import failed and existing instances have failed, add to list of failures.
         * 2. If import succeeded, clear the failed imports for this datasource. (Hiding error tooltip)
         */
        if (!hasImportSucceeded && foundDataSource?.failedImports) {
            foundDataSource.failedImports = [...(foundDataSource.failedImports || []), instanceConfig];
        } else if (hasImportSucceeded && foundDataSource?.failedImports) {
            foundDataSource.failedImports = undefined;
        }

        return acc;
    }, [] as SplashScreenDataSource[]);

    return sortBy(splashScreenDatasources, 'name');
};
